import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Avatar,
    Snackbar,
    Alert,
    Card,
    CardContent,
    Tabs,
    Tab,
    Autocomplete,
    Checkbox,
    CircularProgress,
    Stack,
    Divider,
    tooltipClasses,
    TooltipProps,
} from '@mui/material';
import {
    Add,
    Search,
    Edit,
    Delete,
    WorkOutline,
    Pending,
    HourglassEmpty,
    CheckCircle,
    Cancel,
    Send,
    LocationCity,
    Home,
    RestartAlt,
    CalendarToday,
    Visibility,
    PictureAsPdf,
    Business,
    SwapHoriz,
    Close,
    CloudUpload,
    DeleteOutline,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';
import {
    developmentActivityService,
    employmentActivityService,
    BaseActivity,
    EmploymentCompany,
} from '../../services/ActivityService';
import VillageAutocomplete from '../../components/common/VillageAutocomplete';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

const statusConfig: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; icon: React.ReactElement }> = {
    pending: { label: 'Pending', color: 'warning', icon: <Pending /> },
    requested: { label: 'Requested', color: 'info', icon: <Send /> },
    in_progress: { label: 'In Progress', color: 'primary', icon: <HourglassEmpty /> },
    completed: { label: 'Completed', color: 'success', icon: <CheckCircle /> },
    rejected: { label: 'Rejected', color: 'error', icon: <Cancel /> },
};

interface LocationOption {
    id: number;
    name: string;
}

// ============ DEVELOPMENT FORM INTERFACE ============
interface DevFormData {
    constituency_id: number | '';
    department_id: number | '';
    ministry_id: number | '';
    requested_date: string;
    requested_fund: number | '';
    approved_fund: number | '';
    released_fund: number | '';
    activity: string;
    description: string;
    status: string;
    villages: any[];
}

const initialDevForm: DevFormData = {
    constituency_id: '',
    department_id: '',
    ministry_id: '',
    requested_date: '',
    requested_fund: '',
    approved_fund: '',
    released_fund: '',
    activity: '',
    description: '',
    status: 'pending',
    villages: [],
};

// ============ EMPLOYMENT FORM INTERFACE ============
interface EmpFormData {
    constituency_id: number | '';
    description: string;
    organizer: string;
    job_mela_started_at: string;
    job_mela_ended_at: string;
    number_of_jobs_created: number | '';
    villages: any[];
    companies: { company_name: string; company_address: string; contact_person: string; contact_phone: string; number_of_jobs_created: number | '' }[];
}

const initialEmpForm: EmpFormData = {
    constituency_id: '',
    description: '',
    organizer: '',
    job_mela_started_at: '',
    job_mela_ended_at: '',
    number_of_jobs_created: '',
    villages: [],
    companies: [],
};

// ============ MAIN TAB TYPE ============
type MainTab = 'development' | 'employment';

const DevelopmentActivities = () => {
    const queryClient = useQueryClient();
    const { hasPermission } = useAuth();

    // ============ STATE ============
    const [mainTab, setMainTab] = useState<MainTab>('development');
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
        open: false, message: '', severity: 'success',
    });
    const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning') => {
        setSnackbar({ open: true, message, severity });
    };

    // Pagination & Search
    const [devPageModel, setDevPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [empPageModel, setEmpPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [selectedStatusTab, setSelectedStatusTab] = useState('all');

    // Date Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Location Filters
    const [selectedConstituencies, setSelectedConstituencies] = useState<LocationOption[]>([]);
    const [selectedVillages, setSelectedVillages] = useState<LocationOption[]>([]);

    const filterCount = selectedConstituencies.length + selectedVillages.length + (startDate ? 1 : 0) + (endDate ? 1 : 0);

    const handleResetFilters = () => {
        setSelectedConstituencies([]);
        setSelectedVillages([]);
        setStartDate('');
        setEndDate('');
    };

    const handleQuickFilter = (days: number) => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - days);
        setEndDate(end.toISOString().split('T')[0]);
        setStartDate(start.toISOString().split('T')[0]);
    };

    // ============ DIALOG STATE ============
    // Create/Edit Dialog - Development
    const [devDialog, setDevDialog] = useState(false);
    const [devEditing, setDevEditing] = useState(false);
    const [devSelectedId, setDevSelectedId] = useState<number | null>(null);
    const [devForm, setDevForm] = useState<DevFormData>(initialDevForm);

    // Create/Edit Dialog - Employment
    const [empDialog, setEmpDialog] = useState(false);
    const [empEditing, setEmpEditing] = useState(false);
    const [empSelectedId, setEmpSelectedId] = useState<number | null>(null);
    const [empForm, setEmpForm] = useState<EmpFormData>(initialEmpForm);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; activityId: number | null; type: MainTab }>({
        open: false, activityId: null, type: 'development',
    });

    // View detail dialog - shared
    const [viewDialog, setViewDialog] = useState<{ open: boolean; activity: BaseActivity | null; loading: boolean }>({
        open: false, activity: null, loading: false,
    });

    // Status update dialog - development only
    const [statusDialog, setStatusDialog] = useState<{ open: boolean; activity: BaseActivity | null }>({
        open: false, activity: null,
    });
    const [newStatus, setNewStatus] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [approvedBy, setApprovedBy] = useState('');

    // Image upload dialog
    const [imageUploadDialog, setImageUploadDialog] = useState<{ open: boolean; activityId: number | null; type: MainTab }>({
        open: false, activityId: null, type: 'development',
    });
    const [selectedImages, setSelectedImages] = useState<File[]>([]);

    // ============ QUERIES ============
    const pageModel = mainTab === 'development' ? devPageModel : empPageModel;
    const setPageModel = mainTab === 'development' ? setDevPageModel : setEmpPageModel;

    const { data: devResponse, isLoading: devLoading } = useQuery({
        queryKey: ['development-activities', devPageModel.page, devPageModel.pageSize, debouncedSearch, selectedStatusTab,
            startDate, endDate, selectedConstituencies.map(c => c.id), selectedVillages.map(v => v.id)],
        queryFn: async () => {
            const params: any = {
                page: devPageModel.page + 1,
                per_page: devPageModel.pageSize,
            };
            if (debouncedSearch) params.search = debouncedSearch;
            if (selectedStatusTab !== 'all') params.status = selectedStatusTab;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (selectedConstituencies.length > 0) {
                params['constituency_id[]'] = selectedConstituencies.map(c => c.id);
            }
            if (selectedVillages.length > 0) {
                params['village_id[]'] = selectedVillages.map(v => v.id);
            }
            return developmentActivityService.getAll(params);
        },
        placeholderData: keepPreviousData,
        enabled: mainTab === 'development',
    });

    const { data: empResponse, isLoading: empLoading } = useQuery({
        queryKey: ['employment-activities', empPageModel.page, empPageModel.pageSize, debouncedSearch,
            startDate, endDate, selectedConstituencies.map(c => c.id), selectedVillages.map(v => v.id)],
        queryFn: async () => {
            const params: any = {
                page: empPageModel.page + 1,
                per_page: empPageModel.pageSize,
            };
            if (debouncedSearch) params.search = debouncedSearch;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (selectedConstituencies.length > 0) {
                params['constituency_id[]'] = selectedConstituencies.map(c => c.id);
            }
            if (selectedVillages.length > 0) {
                params['village_id[]'] = selectedVillages.map(v => v.id);
            }
            return employmentActivityService.getAll(params);
        },
        placeholderData: keepPreviousData,
        enabled: mainTab === 'employment',
    });

    // Filter dropdown data
    const { data: constituenciesData = [], isLoading: constituenciesLoading } = useQuery({
        queryKey: ['constituencies-filter'],
        queryFn: async () => {
            const res = await api.get('/tenant/constituencies', { params: { per_page: 100 } });
            return res.data.data.data || res.data.data || [];
        }
    });

    // Form dropdown data
    const { data: formConstituencies = [] } = useQuery({
        queryKey: ['constituencies-form'],
        queryFn: async () => {
            const res = await api.get('/tenant/constituencies', { params: { per_page: 1000 } });
            return res.data?.data?.data || [];
        },
        enabled: devDialog || empDialog,
    });

    const { data: departmentsData } = useQuery({
        queryKey: ['tenant-departments-all'],
        queryFn: async () => {
            const res = await api.get('/tenant/complaint-departments', { params: { per_page: 100 } });
            return res.data?.data?.data || [];
        },
        enabled: devDialog,
    });

    const { data: ministriesData } = useQuery({
        queryKey: ['tenant-ministries-all'],
        queryFn: async () => {
            const res = await api.get('/tenant/ministries', { params: { per_page: 100 } });
            return res.data?.data?.data || [];
        },
        enabled: devDialog,
    });

    // ============ DERIVED DATA ============
    const devActivities = devResponse?.data?.data || [];
    const devTotalCount = devResponse?.data?.total || 0;
    const devCounts = devResponse?.counts || { all: 0, pending: 0, requested: 0, in_progress: 0, completed: 0, rejected: 0 };

    const empActivities = empResponse?.data?.data || [];
    const empTotalCount = empResponse?.data?.total || 0;

    // ============ MUTATIONS ============
    const devSaveMutation = useMutation({
        mutationFn: (data: any) => {
            const payload = { ...data, villages: (data.villages || []).map((v: any) => v.id) };
            return devEditing && devSelectedId
                ? developmentActivityService.update(devSelectedId, payload)
                : developmentActivityService.create(payload);
        },
        onSuccess: () => {
            showSnackbar(`Development Activity ${devEditing ? 'updated' : 'created'} successfully`, 'success');
            queryClient.invalidateQueries({ queryKey: ['development-activities'] });
            handleCloseDevDialog();
        },
        onError: (error: any) => {
            showSnackbar(error.response?.data?.message || 'Something went wrong', 'error');
        }
    });

    const empSaveMutation = useMutation({
        mutationFn: (data: any) => {
            const payload = { ...data, villages: (data.villages || []).map((v: any) => v.id) };
            return empEditing && empSelectedId
                ? employmentActivityService.update(empSelectedId, payload)
                : employmentActivityService.create(payload);
        },
        onSuccess: () => {
            showSnackbar(`Employment Activity ${empEditing ? 'updated' : 'created'} successfully`, 'success');
            queryClient.invalidateQueries({ queryKey: ['employment-activities'] });
            handleCloseEmpDialog();
        },
        onError: (error: any) => {
            showSnackbar(error.response?.data?.message || 'Something went wrong', 'error');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: ({ id, type }: { id: number; type: MainTab }) => {
            return type === 'development'
                ? developmentActivityService.delete(id)
                : employmentActivityService.delete(id);
        },
        onSuccess: () => {
            showSnackbar('Activity deleted successfully', 'success');
            queryClient.invalidateQueries({ queryKey: [mainTab === 'development' ? 'development-activities' : 'employment-activities'] });
            handleCloseDeleteDialog();
        },
        onError: (error: any) => {
            showSnackbar(error.response?.data?.message || 'Failed to delete activity', 'error');
        }
    });

    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status, rejected_reason, approved_by: ab }: { id: number; status: string; rejected_reason?: string; approved_by?: string }) => {
            return developmentActivityService.updateStatus(id, { status, rejected_reason, approved_by: ab });
        },
        onSuccess: () => {
            showSnackbar('Status updated successfully', 'success');
            queryClient.invalidateQueries({ queryKey: ['development-activities'] });
            handleCloseStatusDialog();
        },
        onError: (error: any) => {
            showSnackbar(error.response?.data?.message || 'Failed to update status', 'error');
        }
    });

    const uploadImagesMutation = useMutation({
        mutationFn: ({ id, type, formData }: { id: number; type: MainTab; formData: FormData }) => {
            return type === 'development'
                ? developmentActivityService.uploadImages(id, formData)
                : employmentActivityService.uploadImages(id, formData);
        },
        onSuccess: () => {
            showSnackbar('Images uploaded successfully', 'success');
            queryClient.invalidateQueries({ queryKey: [mainTab === 'development' ? 'development-activities' : 'employment-activities'] });
            handleCloseImageUploadDialog();
            // Refresh view dialog so new images appear immediately
            if (viewDialog.activity) {
                handleViewActivity(viewDialog.activity.id, mainTab, true);
            }
        },
        onError: (error: any) => {
            showSnackbar(error.response?.data?.message || 'Failed to upload images', 'error');
        }
    });

    const deleteImageMutation = useMutation({
        mutationFn: ({ activityId, attachmentId, type }: { activityId: number; attachmentId: number; type: MainTab }) => {
            return type === 'development'
                ? developmentActivityService.deleteImage(activityId, attachmentId)
                : employmentActivityService.deleteImage(activityId, attachmentId);
        },
        onSuccess: () => {
            showSnackbar('Image deleted', 'success');
            queryClient.invalidateQueries({ queryKey: [mainTab === 'development' ? 'development-activities' : 'employment-activities'] });
            // Refresh view dialog
            if (viewDialog.activity) {
                handleViewActivity(viewDialog.activity.id, mainTab, true);
            }
        },
        onError: (error: any) => {
            showSnackbar(error.response?.data?.message || 'Failed to delete image', 'error');
        }
    });

    // ============ HANDLERS ============
    const handleMainTabChange = (_: React.SyntheticEvent, newValue: MainTab) => {
        setMainTab(newValue);
        setSearch('');
        setSelectedStatusTab('all');
    };

    const handleStatusTabChange = (_: React.SyntheticEvent, newValue: string) => {
        setSelectedStatusTab(newValue);
        setDevPageModel({ ...devPageModel, page: 0 });
    };

    // Dev dialog handlers
    const handleOpenDevDialog = (activity?: BaseActivity) => {
        if (activity) {
            const dev = activity.development_activity;
            setDevEditing(true);
            setDevSelectedId(activity.id);
            setDevForm({
                constituency_id: activity.constituency_id || '',
                department_id: dev?.department_id || '',
                ministry_id: dev?.ministry_id || '',
                requested_date: dev?.requested_date ? dev.requested_date.split('T')[0] : '',
                requested_fund: dev?.requested_fund || '',
                approved_fund: dev?.approved_fund || '',
                released_fund: dev?.released_fund || '',
                activity: dev?.activity || '',
                description: activity.description || '',
                status: dev?.status || 'pending',
                villages: activity.villages || [],
            });
        } else {
            setDevEditing(false);
            setDevSelectedId(null);
            setDevForm(initialDevForm);
        }
        setDevDialog(true);
    };

    const handleCloseDevDialog = () => {
        setDevDialog(false);
        setDevForm(initialDevForm);
    };

    const handleDevSubmit = () => {
        if (!devForm.activity || !devForm.constituency_id) {
            showSnackbar('Please fill in required fields (Name and Constituency)', 'warning');
            return;
        }
        devSaveMutation.mutate(devForm);
    };

    // Employment dialog handlers
    const handleOpenEmpDialog = (activity?: BaseActivity) => {
        if (activity) {
            const emp = activity.employment_creation_activity;
            setEmpEditing(true);
            setEmpSelectedId(activity.id);
            setEmpForm({
                constituency_id: activity.constituency_id || '',
                description: activity.description || '',
                organizer: emp?.organizer || '',
                job_mela_started_at: emp?.job_mela_started_at ? emp.job_mela_started_at.split('T')[0] : '',
                job_mela_ended_at: emp?.job_mela_ended_at ? emp.job_mela_ended_at.split('T')[0] : '',
                number_of_jobs_created: emp?.number_of_jobs_created || '',
                villages: activity.villages || [],
                companies: emp?.companies?.map((c: EmploymentCompany) => ({
                    company_name: c.company_name,
                    company_address: c.company_address || '',
                    contact_person: c.contact_person || '',
                    contact_phone: c.contact_phone || '',
                    number_of_jobs_created: c.number_of_jobs_created || '',
                })) || [],
            });
        } else {
            setEmpEditing(false);
            setEmpSelectedId(null);
            setEmpForm(initialEmpForm);
        }
        setEmpDialog(true);
    };

    const handleCloseEmpDialog = () => {
        setEmpDialog(false);
        setEmpForm(initialEmpForm);
    };

    const handleEmpSubmit = () => {
        if (!empForm.constituency_id) {
            showSnackbar('Please select a constituency', 'warning');
            return;
        }
        empSaveMutation.mutate(empForm);
    };

    const addCompanyRow = () => {
        setEmpForm({
            ...empForm,
            companies: [...empForm.companies, { company_name: '', company_address: '', contact_person: '', contact_phone: '', number_of_jobs_created: '' }],
        });
    };

    const removeCompanyRow = (index: number) => {
        setEmpForm({ ...empForm, companies: empForm.companies.filter((_, i) => i !== index) });
    };

    const updateCompanyRow = (index: number, field: string, value: any) => {
        const updated = [...empForm.companies];
        updated[index] = { ...updated[index], [field]: value };
        setEmpForm({ ...empForm, companies: updated });
    };

    // Delete dialog
    const handleOpenDeleteDialog = (id: number, type: MainTab) => {
        setDeleteDialog({ open: true, activityId: id, type });
    };
    const handleCloseDeleteDialog = () => {
        setDeleteDialog({ open: false, activityId: null, type: 'development' });
    };
    const handleDeleteConfirm = () => {
        if (deleteDialog.activityId) {
            deleteMutation.mutate({ id: deleteDialog.activityId, type: deleteDialog.type });
        }
    };

    // View dialog
    const handleViewActivity = async (id: number, type: MainTab, silent = false) => {
        if (!silent) setViewDialog({ open: true, activity: null, loading: true });
        try {
            const res = type === 'development'
                ? await developmentActivityService.getById(id)
                : await employmentActivityService.getById(id);
            setViewDialog({ open: true, activity: res.data, loading: false });
        } catch {
            if (!silent) {
                showSnackbar('Failed to load activity details', 'error');
                setViewDialog({ open: false, activity: null, loading: false });
            }
        }
    };
    const handleCloseViewDialog = () => {
        setViewDialog({ open: false, activity: null, loading: false });
    };

    // Status dialog - development only
    const handleOpenStatusDialog = (activity: BaseActivity) => {
        setStatusDialog({ open: true, activity });
        setNewStatus(activity.development_activity?.status || 'pending');
        setRejectionReason('');
        setApprovedBy('');
    };
    const handleCloseStatusDialog = () => {
        setStatusDialog({ open: false, activity: null });
        setNewStatus('');
        setRejectionReason('');
        setApprovedBy('');
    };
    const handleUpdateStatus = () => {
        if (!statusDialog.activity) return;
        updateStatusMutation.mutate({
            id: statusDialog.activity.id,
            status: newStatus,
            rejected_reason: newStatus === 'rejected' ? rejectionReason : undefined,
            approved_by: newStatus === 'completed' ? approvedBy : undefined,
        });
    };

    // Image upload dialog
    const handleOpenImageUploadDialog = (id: number, type: MainTab) => {
        setImageUploadDialog({ open: true, activityId: id, type });
        setSelectedImages([]);
    };
    const handleCloseImageUploadDialog = () => {
        setImageUploadDialog({ open: false, activityId: null, type: 'development' });
        setSelectedImages([]);
    };
    const handleImageUpload = () => {
        if (!imageUploadDialog.activityId || selectedImages.length === 0) return;
        const formData = new FormData();
        selectedImages.forEach((img) => formData.append('images[]', img));
        uploadImagesMutation.mutate({ id: imageUploadDialog.activityId, type: imageUploadDialog.type, formData });
    };

    // PDF
    const handlePdf = async (id: number, type: MainTab) => {
        try {
            const blob = type === 'development'
                ? await developmentActivityService.getPdf(id)
                : await employmentActivityService.getPdf(id);
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');
        } catch {
            showSnackbar('Failed to generate PDF', 'error');
        }
    };

    // Reset page on filter change
    React.useEffect(() => {
        setDevPageModel(prev => ({ ...prev, page: 0 }));
        setEmpPageModel(prev => ({ ...prev, page: 0 }));
    }, [debouncedSearch, selectedConstituencies, selectedVillages, startDate, endDate]);

    const stringToColor = (string: string) => {
        const colors = [
            ['#7c3aed', '#ede9fe'], // Violet
            ['#0f766e', '#ccfbf1'], // Teal
            ['#4338ca', '#e0e7ff'], // Indigo
            ['#1d4ed8', '#dbeafe'], // Blue
            ['#be185d', '#fce7f3'], // Pink
            ['#b45309', '#fef3c7'], // Amber
        ];
        let hash = 0;
        for (let i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const devStatusStyles: Record<string, { bgcolor: string; color: string }> = {
        pending: { bgcolor: '#fef3c7', color: '#b45309' },
        requested: { bgcolor: '#dbeafe', color: '#1d4ed8' },
        in_progress: { bgcolor: '#e0e7ff', color: '#4338ca' },
        completed: { bgcolor: '#dcfce7', color: '#16a34a' },
        rejected: { bgcolor: '#fee2e2', color: '#dc2626' },
    };

    // ============ DEVELOPMENT COLUMNS ============
    const devColumns: GridColDef[] = [
        {
            field: 'index',
            headerName: '#',
            width: 60,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', opacity: 0.7 }}>
                    {(devPageModel.page * devPageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1}
                </Typography>
            ),
        },
        {
            field: 'activity',
            headerName: 'Activity Name',
            minWidth: 220,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                const activityName = params.row.development_activity?.activity || 'Activity';
                const [darkColor, lightColor] = stringToColor(activityName);
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 0.5,
                            borderRadius: 2,
                            width: '100%',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                        onClick={() => handleViewActivity(params.row.id, 'development')}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: lightColor,
                                color: darkColor,
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                border: `1px solid ${darkColor}20`
                            }}
                        >
                            {activityName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a', letterSpacing: '-0.01em', '&:hover': { color: 'primary.main' } }}>
                                {activityName}
                            </Typography>
                            <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                                {new Date(params.row.created_at).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'constituency',
            headerName: 'Constituency',
            width: 170,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.row.constituency?.name || 'N/A'}
                    size="small"
                    variant="outlined"
                    sx={{
                        borderRadius: 1.5,
                        fontWeight: 500,
                        borderColor: 'divider',
                        bgcolor: 'rgba(0,0,0,0.01)',
                        maxWidth: '100%',
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    }}
                />
            ),
        },
        {
            field: 'villages',
            headerName: 'Villages',
            width: 180,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden' }}>
                    {params.row.villages?.length > 0 ? (
                        <>
                            {params.row.villages.slice(0, 2).map((v: any) => (
                                <Chip key={v.id} label={v.name} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem', borderRadius: '6px', borderColor: 'divider', maxWidth: 80, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }} />
                            ))}
                            {params.row.villages.length > 2 && (
                                <Chip label={`+${params.row.villages.length - 2}`} size="small" sx={{ height: 22, fontSize: '0.7rem', borderRadius: '6px', bgcolor: '#f1f5f9', color: '#64748b' }} />
                            )}
                        </>
                    ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'funds',
            headerName: 'Funds (Req / Appr)',
            width: 180,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const dev = params.row.development_activity;
                return (
                    <Box>
                        <Typography variant="body2" fontFamily="monospace" fontWeight={500} sx={{ color: '#475569' }}>
                            Req: ₹{dev?.requested_fund || 0}
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600} sx={{ color: '#16a34a' }}>
                            Appr: ₹{dev?.approved_fund || 0}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'department',
            headerName: 'Dept / Ministry',
            width: 200,
            renderCell: (params: GridRenderCellParams) => {
                const dev = params.row.development_activity;
                return (
                    <Box sx={{ overflow: 'hidden', width: '100%' }}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#0f172a', letterSpacing: '-0.01em' }}>
                            {dev?.department?.name || '---'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {dev?.ministry?.ministry_name || '---'}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 140,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const status = params.row.development_activity?.status;
                const cfg = statusConfig[status];
                const style = devStatusStyles[status] || devStatusStyles.pending;
                return (
                    <Chip
                        icon={cfg?.icon}
                        label={cfg?.label || status}
                        size="small"
                        sx={{
                            borderRadius: '6px',
                            fontWeight: 600,
                            minWidth: 100,
                            ...style,
                            '& .MuiChip-icon': { color: 'inherit' }
                        }}
                    />
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Stack direction="row" spacing={0.5}>
                    <BootstrapTooltip title="View" placement="top">
                        <IconButton size="small" onClick={() => handleViewActivity(params.row.id, 'development')}
                            sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', '&:hover': { bgcolor: '#dbeafe' } }}>
                            <Visibility fontSize="small" />
                        </IconButton>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Status" placement="top">
                        <IconButton size="small" onClick={() => handleOpenStatusDialog(params.row)}
                            sx={{ bgcolor: '#fef3c7', color: '#b45309', '&:hover': { bgcolor: '#fde68a' } }}>
                            <SwapHoriz fontSize="small" />
                        </IconButton>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Edit" placement="top">
                        <IconButton size="small" onClick={() => handleOpenDevDialog(params.row)}
                            sx={{ bgcolor: '#dcfce7', color: '#16a34a', '&:hover': { bgcolor: '#bbf7d0' } }}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Delete" placement="top">
                        <IconButton size="small" onClick={() => handleOpenDeleteDialog(params.row.id, 'development')}
                            sx={{ bgcolor: '#fee2e2', color: '#dc2626', '&:hover': { bgcolor: '#fecaca' } }}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </BootstrapTooltip>
                </Stack>
            ),
        },
    ];

    // ============ EMPLOYMENT COLUMNS ============
    const empColumns: GridColDef[] = [
        {
            field: 'index',
            headerName: '#',
            width: 60,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', opacity: 0.7 }}>
                    {(empPageModel.page * empPageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1}
                </Typography>
            ),
        },
        {
            field: 'organizer',
            headerName: 'Organizer',
            minWidth: 200,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                const emp = params.row.employment_creation_activity;
                const organizerName = emp?.organizer || 'Organizer';
                const [darkColor, lightColor] = stringToColor(organizerName);
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 0.5,
                            borderRadius: 2,
                            width: '100%',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                        onClick={() => handleViewActivity(params.row.id, 'employment')}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: lightColor,
                                color: darkColor,
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                border: `1px solid ${darkColor}20`
                            }}
                        >
                            {organizerName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a', letterSpacing: '-0.01em', '&:hover': { color: 'primary.main' } }}>
                                {organizerName}
                            </Typography>
                            <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                                {new Date(params.row.created_at).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'constituency',
            headerName: 'Constituency',
            width: 160,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.row.constituency?.name || 'N/A'}
                    size="small"
                    variant="outlined"
                    sx={{
                        borderRadius: 1.5,
                        fontWeight: 500,
                        borderColor: 'divider',
                        bgcolor: 'rgba(0,0,0,0.01)',
                        maxWidth: '100%',
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    }}
                />
            ),
        },
        {
            field: 'villages',
            headerName: 'Villages',
            width: 170,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden' }}>
                    {params.row.villages?.length > 0 ? (
                        <>
                            {params.row.villages.slice(0, 1).map((v: any) => (
                                <Chip key={v.id} label={v.name} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem', borderRadius: '6px', borderColor: 'divider', maxWidth: 90, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }} />
                            ))}
                            {params.row.villages.length > 1 && (
                                <Chip label={`+${params.row.villages.length - 1}`} size="small" sx={{ height: 22, fontSize: '0.7rem', borderRadius: '6px', bgcolor: '#f1f5f9', color: '#64748b' }} />
                            )}
                        </>
                    ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'jobs',
            headerName: 'Jobs Created',
            width: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.row.employment_creation_activity?.number_of_jobs_created || 0}
                    size="small"
                    sx={{
                        fontWeight: 600,
                        minWidth: 60,
                        borderRadius: '6px',
                        bgcolor: '#dcfce7',
                        color: '#16a34a',
                    }}
                />
            ),
        },
        {
            field: 'companies',
            headerName: 'Companies',
            width: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.row.employment_creation_activity?.companies?.length || 0}
                    size="small"
                    sx={{
                        fontWeight: 600,
                        minWidth: 50,
                        borderRadius: '6px',
                        bgcolor: '#eff6ff',
                        color: '#1d4ed8',
                    }}
                />
            ),
        },
        {
            field: 'dates',
            headerName: 'Event Dates',
            width: 180,
            renderCell: (params: GridRenderCellParams) => {
                const emp = params.row.employment_creation_activity;
                return (
                    <Box>
                        <Typography variant="body2" fontFamily="monospace" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            Start: {emp?.job_mela_started_at ? new Date(emp.job_mela_started_at).toLocaleDateString() : '—'}
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            End: {emp?.job_mela_ended_at ? new Date(emp.job_mela_ended_at).toLocaleDateString() : '—'}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 160,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Stack direction="row" spacing={0.5}>
                    <BootstrapTooltip title="View" placement="top">
                        <IconButton size="small" onClick={() => handleViewActivity(params.row.id, 'employment')}
                            sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', '&:hover': { bgcolor: '#dbeafe' } }}>
                            <Visibility fontSize="small" />
                        </IconButton>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Edit" placement="top">
                        <IconButton size="small" onClick={() => handleOpenEmpDialog(params.row)}
                            sx={{ bgcolor: '#dcfce7', color: '#16a34a', '&:hover': { bgcolor: '#bbf7d0' } }}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Delete" placement="top">
                        <IconButton size="small" onClick={() => handleOpenDeleteDialog(params.row.id, 'employment')}
                            sx={{ bgcolor: '#fee2e2', color: '#dc2626', '&:hover': { bgcolor: '#fecaca' } }}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </BootstrapTooltip>
                </Stack>
            ),
        },
    ];

    const dataGridSx = {
        border: 'none',
        '& .MuiDataGrid-columnHeaders': {
            bgcolor: '#f8fafc',
            borderBottom: '1px solid',
            borderColor: 'divider',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            color: '#475569',
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
        '& .MuiDataGrid-cell': {
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            minHeight: 60,
        },
        '& .MuiDataGrid-row:hover': {
            bgcolor: '#f8fafc',
        },
    };

    // ============ RENDER ============
    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: mainTab === 'development'
                        ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                        : 'linear-gradient(135deg, #065f46 0%, #064e3b 100%)',
                    color: 'white',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                            {mainTab === 'development' ? <WorkOutline sx={{ fontSize: 32 }} /> : <Business sx={{ fontSize: 32 }} />}
                        </Avatar>
                        <Box>
                            <Typography variant="h4" fontWeight="bold">Activities</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {mainTab === 'development'
                                    ? 'Manage infrastructure and development projects'
                                    : 'Manage employment creation and job mela activities'
                                }
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => mainTab === 'development' ? handleOpenDevDialog() : handleOpenEmpDialog()}
                        sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
                    >
                        Add {mainTab === 'development' ? 'Development' : 'Employment'} Activity
                    </Button>
                </Box>
            </Paper>

            {/* Main Tabs: Development / Employment */}
            <Paper sx={{ mb: 3, borderRadius: 2 }}>
                <Tabs
                    value={mainTab}
                    onChange={handleMainTabChange}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab
                        icon={<WorkOutline sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label="Development Activities"
                        value="development"
                        sx={{ fontWeight: 600 }}
                    />
                    <Tab
                        icon={<Business sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label="Employment Creation"
                        value="employment"
                        sx={{ fontWeight: 600 }}
                    />
                </Tabs>
            </Paper>

            {/* Search */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    placeholder={mainTab === 'development' ? 'Search activities...' : 'Search by organizer, companies...'}
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 350 }, bgcolor: 'background.paper', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Filters Card */}
            <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LocationCity fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight="bold" color="primary">Filters</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>(Multi-select enabled)</Typography>
                        {filterCount > 0 && (
                            <Button size="small" startIcon={<RestartAlt />} onClick={handleResetFilters} sx={{ ml: 'auto' }}>
                                Clear ({filterCount})
                            </Button>
                        )}
                    </Box>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={constituenciesData || []}
                                getOptionLabel={(option: LocationOption) => option.name}
                                value={selectedConstituencies}
                                onChange={(_, newValue) => setSelectedConstituencies(newValue)}
                                loading={constituenciesLoading}
                                limitTags={2}
                                disableCloseOnSelect
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}><Checkbox size="small" style={{ marginRight: 8 }} checked={selected} />{option.name}</li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Constituencies"
                                        placeholder={selectedConstituencies.length === 0 ? "All" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start"><LocationCity fontSize="small" color="action" /></InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {constituenciesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <VillageAutocomplete
                                multiple
                                value={selectedVillages}
                                onChange={(newValue: any) => setSelectedVillages(newValue)}
                                constituencyIds={selectedConstituencies.map(c => c.id)}
                                limitTags={2}
                                label="Villages"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField type="date" label="From Date" size="small" fullWidth value={startDate}
                                onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday fontSize="small" color="action" /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField type="date" label="To Date" size="small" fullWidth value={endDate}
                                onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday fontSize="small" color="action" /></InputAdornment> }}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip label="7 Days" size="small" onClick={() => handleQuickFilter(7)} clickable variant="outlined" />
                        <Chip label="15 Days" size="small" onClick={() => handleQuickFilter(15)} clickable variant="outlined" />
                        <Chip label="30 Days" size="small" onClick={() => handleQuickFilter(30)} clickable variant="outlined" />
                        <Chip label="90 Days" size="small" onClick={() => handleQuickFilter(90)} clickable variant="outlined" />
                    </Box>
                </CardContent>
            </Card>

            {/* Status Tabs - Development only */}
            {mainTab === 'development' && (
                <Paper sx={{ mb: 3, borderRadius: 2 }}>
                    <Tabs
                        value={selectedStatusTab}
                        onChange={handleStatusTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab label={`All (${devCounts.all})`} value="all" />
                        <Tab icon={<Pending sx={{ fontSize: 18 }} />} iconPosition="start" label={`Pending (${devCounts.pending})`} value="pending" sx={{ color: 'warning.main' }} />
                        <Tab icon={<Send sx={{ fontSize: 18 }} />} iconPosition="start" label={`Requested (${devCounts.requested})`} value="requested" sx={{ color: 'info.main' }} />
                        <Tab icon={<HourglassEmpty sx={{ fontSize: 18 }} />} iconPosition="start" label={`In Progress (${devCounts.in_progress})`} value="in_progress" sx={{ color: 'primary.main' }} />
                        <Tab icon={<CheckCircle sx={{ fontSize: 18 }} />} iconPosition="start" label={`Completed (${devCounts.completed})`} value="completed" sx={{ color: 'success.main' }} />
                        <Tab icon={<Cancel sx={{ fontSize: 18 }} />} iconPosition="start" label={`Rejected (${devCounts.rejected})`} value="rejected" sx={{ color: 'error.main' }} />
                    </Tabs>
                </Paper>
            )}

            {/* Data Grid */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
                {mainTab === 'development' ? (
                    <DataGrid
                        rows={devActivities}
                        columns={devColumns}
                        loading={devLoading}
                        paginationModel={devPageModel}
                        onPaginationModelChange={setDevPageModel}
                        pageSizeOptions={[10, 25, 50]}
                        rowCount={devTotalCount}
                        paginationMode="server"
                        disableRowSelectionOnClick
                        autoHeight
                        getRowHeight={() => 'auto'}
                        sx={dataGridSx}
                    />
                ) : (
                    <DataGrid
                        rows={empActivities}
                        columns={empColumns}
                        loading={empLoading}
                        paginationModel={empPageModel}
                        onPaginationModelChange={setEmpPageModel}
                        pageSizeOptions={[10, 25, 50]}
                        rowCount={empTotalCount}
                        paginationMode="server"
                        disableRowSelectionOnClick
                        autoHeight
                        getRowHeight={() => 'auto'}
                        sx={dataGridSx}
                    />
                )}
            </Card>

            {/* ============ DEVELOPMENT CREATE/EDIT DIALOG ============ */}
            <Dialog open={devDialog} onClose={handleCloseDevDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', pb: 2 }}>
                    {devEditing ? 'Edit Development Activity' : 'Add New Development Activity'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={3} sx={{ mt: 0 }}>
                        <Grid size={{ xs: 12 }}>
                            <TextField fullWidth label="Activity Name *" value={devForm.activity}
                                onChange={(e) => setDevForm({ ...devForm, activity: e.target.value })}
                                placeholder="E.g. Road Construction phase 1"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField fullWidth label="Description" multiline rows={2} value={devForm.description}
                                onChange={(e) => setDevForm({ ...devForm, description: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Constituency *</InputLabel>
                                <Select value={devForm.constituency_id} label="Constituency *"
                                    onChange={(e) => setDevForm({ ...devForm, constituency_id: e.target.value as number })}
                                >
                                    {formConstituencies?.map((c: any) => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <VillageAutocomplete
                                multiple
                                size="medium"
                                value={devForm.villages}
                                onChange={(newValue: any) => setDevForm({ ...devForm, villages: newValue })}
                                label="Select Villages"
                                showIcon={false}
                                enabled={devDialog}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Department</InputLabel>
                                <Select value={devForm.department_id || ''} label="Department"
                                    onChange={(e) => setDevForm({ ...devForm, department_id: e.target.value as number })}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {departmentsData?.map((d: any) => (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Ministry</InputLabel>
                                <Select value={devForm.ministry_id || ''} label="Ministry"
                                    onChange={(e) => setDevForm({ ...devForm, ministry_id: e.target.value as number })}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {ministriesData?.map((m: any) => (<MenuItem key={m.id} value={m.id}>{m.ministry_name}</MenuItem>))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField fullWidth label="Requested Fund (₹)" type="number" value={devForm.requested_fund}
                                onChange={(e) => setDevForm({ ...devForm, requested_fund: e.target.value ? Number(e.target.value) : '' })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField fullWidth label="Approved Fund (₹)" type="number" value={devForm.approved_fund}
                                onChange={(e) => setDevForm({ ...devForm, approved_fund: e.target.value ? Number(e.target.value) : '' })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField fullWidth label="Released Fund (₹)" type="number" value={devForm.released_fund}
                                onChange={(e) => setDevForm({ ...devForm, released_fund: e.target.value ? Number(e.target.value) : '' })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField fullWidth label="Requested Date" type="date" InputLabelProps={{ shrink: true }}
                                value={devForm.requested_date}
                                onChange={(e) => setDevForm({ ...devForm, requested_date: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select value={devForm.status} label="Status"
                                    onChange={(e) => setDevForm({ ...devForm, status: e.target.value })}
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="requested">Requested</MenuItem>
                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={handleCloseDevDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleDevSubmit} variant="contained" disabled={devSaveMutation.isPending} sx={{ px: 4 }}>
                        {devSaveMutation.isPending ? 'Saving...' : 'Save Activity'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============ EMPLOYMENT CREATE/EDIT DIALOG ============ */}
            <Dialog open={empDialog} onClose={handleCloseEmpDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', pb: 2 }}>
                    {empEditing ? 'Edit Employment Activity' : 'Add New Employment Activity'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={3} sx={{ mt: 0 }}>
                        <Grid size={{ xs: 12 }}>
                            <TextField fullWidth label="Description" multiline rows={2} value={empForm.description}
                                onChange={(e) => setEmpForm({ ...empForm, description: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Constituency *</InputLabel>
                                <Select value={empForm.constituency_id} label="Constituency *"
                                    onChange={(e) => setEmpForm({ ...empForm, constituency_id: e.target.value as number })}
                                >
                                    {formConstituencies?.map((c: any) => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <VillageAutocomplete
                                multiple
                                size="medium"
                                value={empForm.villages}
                                onChange={(newValue: any) => setEmpForm({ ...empForm, villages: newValue })}
                                label="Select Villages"
                                showIcon={false}
                                enabled={empDialog}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField fullWidth label="Organizer" value={empForm.organizer}
                                onChange={(e) => setEmpForm({ ...empForm, organizer: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField fullWidth label="Job Mela Start Date" type="date" InputLabelProps={{ shrink: true }}
                                value={empForm.job_mela_started_at}
                                onChange={(e) => setEmpForm({ ...empForm, job_mela_started_at: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField fullWidth label="Job Mela End Date" type="date" InputLabelProps={{ shrink: true }}
                                value={empForm.job_mela_ended_at}
                                onChange={(e) => setEmpForm({ ...empForm, job_mela_ended_at: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField fullWidth label="Total Jobs Created" type="number" value={empForm.number_of_jobs_created}
                                onChange={(e) => setEmpForm({ ...empForm, number_of_jobs_created: e.target.value ? Number(e.target.value) : '' })}
                            />
                        </Grid>

                        {/* Companies section */}
                        <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold">Companies</Typography>
                                <Button size="small" startIcon={<Add />} onClick={addCompanyRow}>Add Company</Button>
                            </Box>
                            {empForm.companies.map((company, index) => (
                                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: 2 }} variant="outlined">
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2">Company {index + 1}</Typography>
                                        <IconButton size="small" color="error" onClick={() => removeCompanyRow(index)}><DeleteOutline fontSize="small" /></IconButton>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField fullWidth size="small" label="Company Name" value={company.company_name}
                                                onChange={(e) => updateCompanyRow(index, 'company_name', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField fullWidth size="small" label="Address" value={company.company_address}
                                                onChange={(e) => updateCompanyRow(index, 'company_address', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <TextField fullWidth size="small" label="Contact Person" value={company.contact_person}
                                                onChange={(e) => updateCompanyRow(index, 'contact_person', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <TextField fullWidth size="small" label="Phone" value={company.contact_phone}
                                                onChange={(e) => updateCompanyRow(index, 'contact_phone', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <TextField fullWidth size="small" label="Jobs Created" type="number" value={company.number_of_jobs_created}
                                                onChange={(e) => updateCompanyRow(index, 'number_of_jobs_created', e.target.value ? Number(e.target.value) : '')}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={handleCloseEmpDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleEmpSubmit} variant="contained" disabled={empSaveMutation.isPending} sx={{ px: 4 }}>
                        {empSaveMutation.isPending ? 'Saving...' : 'Save Activity'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============ VIEW DETAIL DIALOG ============ */}
            <Dialog open={viewDialog.open} onClose={handleCloseViewDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{
                    background: mainTab === 'development'
                        ? 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)'
                        : 'linear-gradient(135deg, #065f46 0%, #064e3b 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.2)' }}>
                            {mainTab === 'development' ? <WorkOutline /> : <Business />}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                {viewDialog.loading ? 'Loading...' : (
                                    mainTab === 'development'
                                        ? viewDialog.activity?.development_activity?.activity || 'Activity Details'
                                        : `Employment Activity #${viewDialog.activity?.id || ''}`
                                )}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {!viewDialog.loading && viewDialog.activity && `Created by: ${viewDialog.activity.creator?.name || 'Unknown'}`}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!viewDialog.loading && viewDialog.activity && (
                            <>
                                <Button
                                    onClick={() => handlePdf(viewDialog.activity!.id, mainTab)}
                                    startIcon={<PictureAsPdf />}
                                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                                    variant="outlined" size="small"
                                >PDF</Button>
                                {mainTab === 'development' && (
                                    <Button
                                        onClick={() => {
                                            handleCloseViewDialog();
                                            handleOpenStatusDialog(viewDialog.activity!);
                                        }}
                                        startIcon={<SwapHoriz />}
                                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                                        variant="outlined" size="small"
                                    >Update Status</Button>
                                )}
                                <Button
                                    onClick={() => handleOpenImageUploadDialog(viewDialog.activity!.id, mainTab)}
                                    startIcon={<CloudUpload />}
                                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                                    variant="outlined" size="small"
                                >Upload Images</Button>
                            </>
                        )}
                        <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}><Close /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {viewDialog.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                    ) : viewDialog.activity && mainTab === 'development' ? (
                        <DevDetailContent activity={viewDialog.activity} onDeleteImage={(attachmentId) =>
                            deleteImageMutation.mutate({ activityId: viewDialog.activity!.id, attachmentId, type: 'development' })
                        } />
                    ) : viewDialog.activity && mainTab === 'employment' ? (
                        <EmpDetailContent activity={viewDialog.activity} onDeleteImage={(attachmentId) =>
                            deleteImageMutation.mutate({ activityId: viewDialog.activity!.id, attachmentId, type: 'employment' })
                        } />
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* ============ STATUS UPDATE DIALOG ============ */}
            <Dialog open={statusDialog.open} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>Update Status</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Activity</Typography>
                            <Typography variant="body1">
                                {statusDialog.activity?.development_activity?.activity || `Activity #${statusDialog.activity?.id}`}
                            </Typography>
                        </Box>
                        <Divider />
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select value={newStatus} label="Status" onChange={(e) => setNewStatus(e.target.value)}>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="requested">Requested</MenuItem>
                                <MenuItem value="in_progress">In Progress</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>
                        {newStatus === 'rejected' && (
                            <TextField fullWidth label="Rejection Reason" value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)} multiline rows={3} required
                            />
                        )}
                        {newStatus === 'completed' && (
                            <TextField fullWidth label="Approved By" value={approvedBy}
                                onChange={(e) => setApprovedBy(e.target.value)}
                                helperText="Enter the name of the person who approved"
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseStatusDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdateStatus}
                        disabled={updateStatusMutation.isPending || (newStatus === 'rejected' && !rejectionReason)}
                    >
                        {updateStatusMutation.isPending ? 'Updating...' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============ IMAGE UPLOAD DIALOG ============ */}
            <Dialog open={imageUploadDialog.open} onClose={handleCloseImageUploadDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>Upload Images</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Max 10 images, max 250KB each. Supported: JPEG, PNG, GIF, WebP.
                        </Typography>
                        <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                            Select Images
                            <input type="file" hidden multiple accept="image/jpeg,image/png,image/gif,image/webp"
                                onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    setSelectedImages(files.slice(0, 10));
                                }}
                            />
                        </Button>
                        {selectedImages.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2">{selectedImages.length} file(s) selected</Typography>
                                {selectedImages.map((f, i) => (
                                    <Chip key={i} label={`${f.name} (${(f.size / 1024).toFixed(1)}KB)`} size="small" sx={{ mr: 0.5, mt: 0.5 }}
                                        onDelete={() => setSelectedImages(selectedImages.filter((_, j) => j !== i))}
                                    />
                                ))}
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseImageUploadDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleImageUpload} disabled={uploadImagesMutation.isPending || selectedImages.length === 0}>
                        {uploadImagesMutation.isPending ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============ DELETE CONFIRMATION DIALOG ============ */}
            <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>Delete Activity</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this activity? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDeleteDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

// ============ DETAIL CONTENT COMPONENTS ============

const SectionTitle = ({ children, color = '#1d4ed8' }: { children: React.ReactNode; color?: string }) => (
    <Typography variant="subtitle2" fontWeight="bold" sx={{ bgcolor: color, color: 'white', px: 2, py: 1, borderRadius: 1, mb: 1 }}>
        {children}
    </Typography>
);

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
    </Box>
);

const DevDetailContent = ({ activity, onDeleteImage }: { activity: BaseActivity; onDeleteImage: (id: number) => void }) => {
    const dev = activity.development_activity;
    if (!dev) return null;

    const cfg = statusConfig[dev.status];

    return (
        <Stack spacing={3}>
            {/* Status & Funds */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: cfg?.color === 'warning' ? '#fef3c7' : cfg?.color === 'success' ? '#dcfce7' : cfg?.color === 'error' ? '#fee2e2' : '#e0e7ff' }}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Typography variant="h6" fontWeight="bold">{cfg?.label || dev.status}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#eff6ff' }}>
                        <Typography variant="caption" color="text.secondary">Requested</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">₹{dev.requested_fund || 0}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdf4' }}>
                        <Typography variant="caption" color="text.secondary">Approved</Typography>
                        <Typography variant="h6" fontWeight="bold" color="success.main">₹{dev.approved_fund || 0}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#faf5ff' }}>
                        <Typography variant="caption" color="text.secondary">Released</Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#7c3aed' }}>₹{dev.released_fund || 0}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Description */}
            {activity.description && (
                <Box>
                    <SectionTitle>Description</SectionTitle>
                    <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#4b5563' }}>{activity.description}</Typography>
                </Box>
            )}

            {/* Details */}
            <Box>
                <SectionTitle>Activity Details</SectionTitle>
                <DetailRow label="Department" value={dev.department?.name} />
                <DetailRow label="Ministry" value={dev.ministry?.ministry_name} />
                <DetailRow label="Constituency" value={activity.constituency?.name} />
                <DetailRow label="Villages" value={activity.villages?.map((v: any) => v.name).join(', ')} />
                <DetailRow label="Requested Date" value={dev.requested_date ? new Date(dev.requested_date).toLocaleDateString() : null} />
                <DetailRow label="Created" value={new Date(activity.created_at).toLocaleDateString()} />
            </Box>

            {/* Approval / Rejection */}
            {(dev.approved_by || dev.rejected_by) && (
                <Box>
                    <SectionTitle color="#16a34a">Approval Information</SectionTitle>
                    {dev.approved_by && <DetailRow label="Approved By" value={dev.approved_by} />}
                    {dev.approved_at && <DetailRow label="Approved At" value={new Date(dev.approved_at).toLocaleDateString()} />}
                    {dev.rejected_by && <DetailRow label="Rejected By" value={<Typography variant="body2" color="error">{dev.rejected_by}</Typography>} />}
                    {dev.rejected_at && <DetailRow label="Rejected At" value={new Date(dev.rejected_at).toLocaleDateString()} />}
                </Box>
            )}

            {dev.status === 'rejected' && dev.rejected_reason && (
                <Box>
                    <SectionTitle color="#dc2626">Rejection Reason</SectionTitle>
                    <Typography variant="body2" sx={{ color: '#991b1b' }}>{dev.rejected_reason}</Typography>
                </Box>
            )}

            {/* Images */}
            {activity.attachments && activity.attachments.length > 0 && (
                <Box>
                    <SectionTitle color="#7c3aed">Images ({activity.attachments.length})</SectionTitle>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {activity.attachments.map((att: any) => (
                            <Box key={att.id} sx={{ position: 'relative', width: 120, height: 120 }}>
                                <img src={att.signed_url} alt="Attachment"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                />
                                <IconButton size="small"
                                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,0,0,0.1)' } }}
                                    onClick={() => onDeleteImage(att.id)}
                                >
                                    <DeleteOutline fontSize="small" color="error" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Stack>
    );
};

const EmpDetailContent = ({ activity, onDeleteImage }: { activity: BaseActivity; onDeleteImage: (id: number) => void }) => {
    const emp = activity.employment_creation_activity;
    if (!emp) return null;

    return (
        <Stack spacing={3}>
            {/* Stats */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ecfdf5' }}>
                        <Typography variant="caption" color="text.secondary">Total Jobs</Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">{emp.number_of_jobs_created || 0}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#eff6ff' }}>
                        <Typography variant="caption" color="text.secondary">Companies</Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary.main">{emp.companies?.length || 0}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#faf5ff' }}>
                        <Typography variant="caption" color="text.secondary">Villages</Typography>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#7c3aed' }}>{activity.villages?.length || 0}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Description */}
            {activity.description && (
                <Box>
                    <SectionTitle color="#059669">Description</SectionTitle>
                    <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#4b5563' }}>{activity.description}</Typography>
                </Box>
            )}

            {/* Details */}
            <Box>
                <SectionTitle color="#059669">Event Details</SectionTitle>
                <DetailRow label="Organizer" value={emp.organizer} />
                <DetailRow label="Job Mela Start" value={emp.job_mela_started_at ? new Date(emp.job_mela_started_at).toLocaleDateString() : null} />
                <DetailRow label="Job Mela End" value={emp.job_mela_ended_at ? new Date(emp.job_mela_ended_at).toLocaleDateString() : null} />
                <DetailRow label="Constituency" value={activity.constituency?.name} />
                <DetailRow label="Villages" value={activity.villages?.map((v: any) => v.name).join(', ')} />
                <DetailRow label="Created" value={new Date(activity.created_at).toLocaleDateString()} />
            </Box>

            {/* Companies */}
            {emp.companies && emp.companies.length > 0 && (
                <Box>
                    <SectionTitle color="#d97706">Participating Companies ({emp.companies.length})</SectionTitle>
                    {emp.companies.map((c: EmploymentCompany) => (
                        <Paper key={c.id} variant="outlined" sx={{ p: 2, mb: 1, borderRadius: 2 }}>
                            <Grid container spacing={1}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2" fontWeight="bold">{c.company_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{c.company_address || '—'}</Typography>
                                </Grid>
                                <Grid size={{ xs: 3 }}>
                                    <Typography variant="caption" color="text.secondary">Contact</Typography>
                                    <Typography variant="body2">{c.contact_person || '—'}</Typography>
                                    <Typography variant="caption">{c.contact_phone || ''}</Typography>
                                </Grid>
                                <Grid size={{ xs: 3 }} sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" color="text.secondary">Jobs</Typography>
                                    <Typography variant="h6" fontWeight="bold" color="success.main">{c.number_of_jobs_created || 0}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                </Box>
            )}

            {/* Images */}
            {activity.attachments && activity.attachments.length > 0 && (
                <Box>
                    <SectionTitle color="#7c3aed">Images ({activity.attachments.length})</SectionTitle>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {activity.attachments.map((att: any) => (
                            <Box key={att.id} sx={{ position: 'relative', width: 120, height: 120 }}>
                                <img src={att.signed_url} alt="Attachment"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                />
                                <IconButton size="small"
                                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,0,0,0.1)' } }}
                                    onClick={() => onDeleteImage(att.id)}
                                >
                                    <DeleteOutline fontSize="small" color="error" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Stack>
    );
};

export default DevelopmentActivities;
